import React from 'react';
import { Box, Typography, Stack, Button, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  CARD_INSET_HORIZONTAL_PX,
  CONTENT_BLOCK_HEIGHT,
  externalFlowContentColumnSx,
  externalFlowGlassCardLayoutSx,
  externalFlowLineClamp3Sx,
  externalFlowNavIconButtonSx,
  externalFlowRequirementSurfaceSx,
  LABEL_PRIMARY,
  LABEL_SECONDARY,
  LETTER_SPACING,
  REQUIREMENT_CTA_STRIP_HEIGHT,
  REQUIREMENT_SUBTITLE_FONT_SIZE,
  REQUIREMENT_TEXT_TOP_PX,
  TITLE_FONT_SIZE,
  UPLOAD_FLOW_PAGE_BG,
  requirementCtaPrimaryButtonSx,
  requirementCtaStripSx,
  requirementIllustrationSx,
} from './uploadCardLayout';

const STEPS = [
  {
    step: 1,
    title: 'Labeling is required',
    subtitle: 'Room labels and dimensions must be readable',
    imageSrc: '/assets/requirement-1.svg',
    imageAlt: 'Floor plan with readable room label and dimensions versus unlabeled plan',
  },
  {
    step: 2,
    title: 'Consistent scale is required',
    subtitle: 'Every floor must use the exact same scale',
    imageSrc: '/assets/requirement-2.svg',
    imageAlt: 'Floor plans at the same scale versus mismatched scales',
  },
  {
    step: 3,
    title: 'Empty plan for best result',
    subtitle: "We can't remove furniture already drawn in",
    imageSrc: '/assets/requirement-3.svg',
    imageAlt: 'Empty labeled floor plan versus plan with furniture drawn in',
  },
];

const RequirementsCard = ({ step = 1, onStepChange, onUploadAnother, onDismiss }) => {
  const index = Math.min(Math.max(step, 1), 3) - 1;
  const content = STEPS[index];
  const isFirst = content.step === 1;
  const isLast = content.step === 3;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 0,
        flexShrink: 0,
        bgcolor: UPLOAD_FLOW_PAGE_BG,
        width: '100%',
      }}
    >
      <Box
        sx={{
          ...externalFlowContentColumnSx,
          minHeight: CONTENT_BLOCK_HEIGHT,
          maxHeight: CONTENT_BLOCK_HEIGHT,
        }}
      >
        <Box
          role="region"
          aria-label={`Floor plan requirement ${content.step} of 3`}
          sx={{
            ...externalFlowGlassCardLayoutSx,
            ...externalFlowRequirementSurfaceSx,
            // Full-bleed graphic + strip nested to card edge (text keeps inset).
            px: 0,
            pt: 0,
            pb: 0,
          }}
        >
          <Box
            component="img"
            src={content.imageSrc}
            alt={content.imageAlt}
            draggable={false}
            sx={requirementIllustrationSx}
          />

          <Stack
            spacing={0}
            alignItems="center"
            sx={{
              width: '100%',
              height: '100%',
              position: 'relative',
              zIndex: 2,
              px: `${CARD_INSET_HORIZONTAL_PX}px`,
              pt: `${REQUIREMENT_TEXT_TOP_PX}px`,
              // Reserve the snapped CTA strip so copy never overlaps actions.
              pb: `${REQUIREMENT_CTA_STRIP_HEIGHT}px`,
              boxSizing: 'border-box',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: LABEL_PRIMARY,
                fontSize: TITLE_FONT_SIZE,
                letterSpacing: LETTER_SPACING,
                lineHeight: 1.2,
                textAlign: 'center',
                mb: 0.35,
                ...externalFlowLineClamp3Sx,
              }}
            >
              {content.title}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: LABEL_SECONDARY,
                fontSize: REQUIREMENT_SUBTITLE_FONT_SIZE,
                letterSpacing: LETTER_SPACING,
                lineHeight: 1.35,
                textAlign: 'center',
                maxWidth: 280,
                whiteSpace: 'nowrap',
              }}
            >
              {content.subtitle}
            </Typography>
          </Stack>

          <Box sx={requirementCtaStripSx}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                height: '100%',
              }}
            >
              {isFirst ? (
                <IconButton
                  size="small"
                  aria-label="Close requirements guide"
                  onClick={() => onDismiss?.()}
                  sx={externalFlowNavIconButtonSx}
                >
                  <ChevronLeftIcon sx={{ fontSize: 20 }} />
                </IconButton>
              ) : (
                <IconButton
                  size="small"
                  aria-label="Previous requirement"
                  onClick={() => onStepChange?.(content.step - 1)}
                  sx={externalFlowNavIconButtonSx}
                >
                  <ChevronLeftIcon sx={{ fontSize: 20 }} />
                </IconButton>
              )}
            </Box>

            {/* Keep dots mounted (hidden on last step) so columns — and chevrons — never shift. */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                visibility: isLast ? 'hidden' : 'visible',
                pointerEvents: isLast ? 'none' : 'auto',
              }}
              aria-hidden={isLast || undefined}
            >
              <Box
                role="tablist"
                aria-label="Requirement steps"
                sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                {STEPS.map((s) => {
                  const active = s.step === content.step;
                  return (
                    <Box
                      key={s.step}
                      role="tab"
                      aria-selected={active}
                      aria-label={`Requirement ${s.step}`}
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: active ? LABEL_PRIMARY : 'rgba(0,0,0,0.18)',
                        flexShrink: 0,
                      }}
                    />
                  );
                })}
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                height: '100%',
              }}
            >
              {isLast ? (
                <Button
                  variant="contained"
                  size="medium"
                  onClick={onUploadAnother}
                  aria-label="Upload floor plan"
                  disableElevation
                  sx={requirementCtaPrimaryButtonSx}
                >
                  Upload floor plan
                </Button>
              ) : (
                <IconButton
                  size="small"
                  aria-label="Next requirement"
                  onClick={() => onStepChange?.(content.step + 1)}
                  sx={externalFlowNavIconButtonSx}
                >
                  <ChevronRightIcon sx={{ fontSize: 20 }} />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RequirementsCard;
